(() => {
  const selector = '[data-why-look-back]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function init(root) {
    if (!root || root.dataset.carouselReady === 'true') return;

    const viewport = root.querySelector('[data-carousel-viewport]');
    const track = root.querySelector('[data-carousel-track]');
    const previous = root.querySelector('[data-carousel-previous]');
    const next = root.querySelector('[data-carousel-next]');
    const cards = [...root.querySelectorAll('.why-look-back__card')];
    const videos = [...root.querySelectorAll('[data-lazy-video]')];

    if (!viewport || !track) return;
    root.dataset.carouselReady = 'true';

    const stallTimers = new WeakMap();
    const allowAutoplay = !reducedMotion.matches;
    let sectionVisible = false;
    let scrollFrame;

    const getShell = (video) => video.closest('[data-video-shell]');
    const setStatus = (video, message = '') => {
      const status = getShell(video)?.querySelector('[data-video-status]');
      if (status) status.textContent = message;
    };

    const clearStallTimer = (video) => {
      window.clearTimeout(stallTimers.get(video));
      stallTimers.delete(video);
    };

    const markLoading = (video) => {
      const shell = getShell(video);
      if (!shell) return;
      shell.classList.add('is-loading');
      shell.classList.remove('is-playing', 'is-stalled');
      setStatus(video, 'Loading video');

      clearStallTimer(video);
      stallTimers.set(video, window.setTimeout(() => {
        if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
          shell.classList.remove('is-loading', 'is-playing');
          shell.classList.add('is-stalled');
          setStatus(video, 'Video is taking longer to load. Press play to retry.');
        }
      }, 9000));
    };

    const markReady = (video) => {
      const shell = getShell(video);
      if (!shell) return;
      clearStallTimer(video);
      shell.classList.add('is-ready');
      shell.classList.remove('is-loading', 'is-stalled');
      setStatus(video);
    };

    const markPlaying = (video) => {
      const shell = getShell(video);
      if (!shell) return;
      markReady(video);
      shell.classList.add('is-playing');
    };

    const markPaused = (video) => {
      const shell = getShell(video);
      if (!shell) return;
      clearStallTimer(video);
      shell.classList.remove('is-loading', 'is-playing', 'is-stalled');
      setStatus(video);
    };

    const markError = (video) => {
      const shell = getShell(video);
      if (!shell) return;
      clearStallTimer(video);
      shell.classList.remove('is-loading', 'is-playing');
      shell.classList.add('is-stalled');
      setStatus(video, 'Video could not load. Press play to retry.');
    };

    const ensureLoaded = (video) => {
      if (video.dataset.loaded === 'true') return;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
      });
      video.dataset.loaded = 'true';
      markLoading(video);
      video.load();
    };

    const pauseAll = () => {
      videos.forEach((video) => {
        if (!video.paused) video.pause();
      });
    };

    const playVideo = async (video) => {
      if (!video) return;
      ensureLoaded(video);
      markLoading(video);

      try {
        await video.play();
      } catch (error) {
        if (error.name !== 'AbortError') markPaused(video);
      }
    };

    const playAll = () => {
      if (!allowAutoplay || !sectionVisible || document.visibilityState !== 'visible') return;
      videos.forEach((video) => {
        if (video.paused) playVideo(video);
      });
    };

    videos.forEach((video) => {
      const shell = getShell(video);
      const playButton = shell?.querySelector('[data-video-play]');

      video.addEventListener('loadstart', () => markLoading(video));
      video.addEventListener('loadeddata', () => markReady(video));
      video.addEventListener('canplay', () => markReady(video));
      video.addEventListener('playing', () => markPlaying(video));
      video.addEventListener('waiting', () => markLoading(video));
      video.addEventListener('stalled', () => markLoading(video));
      video.addEventListener('error', () => markError(video));
      video.addEventListener('pause', () => markPaused(video));

      video.addEventListener('click', () => {
        if (video.paused) {
          playVideo(video);
        } else {
          video.pause();
        }
      });

      playButton?.addEventListener('click', (event) => {
        event.stopPropagation();
        if (video.error) video.load();
        playVideo(video);
      });
    });

    const getScrollAmount = () => {
      const card = cards[0];
      const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      return card ? card.getBoundingClientRect().width + gap : viewport.clientWidth * 0.85;
    };

    const updateControls = () => {
      if (!previous || !next) return;
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      previous.disabled = viewport.scrollLeft <= 2;
      next.disabled = viewport.scrollLeft >= maxScroll - 2;
      root.classList.toggle('why-look-back--no-overflow', maxScroll <= 2);
    };

    previous?.addEventListener('click', () => {
      viewport.scrollBy({ left: -getScrollAmount(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    });

    next?.addEventListener('click', () => {
      viewport.scrollBy({ left: getScrollAmount(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    });

    viewport.addEventListener('scroll', () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(updateControls);
    }, { passive: true });

    if ('ResizeObserver' in window) {
      new ResizeObserver(updateControls).observe(viewport);
    } else {
      window.addEventListener('resize', updateControls);
    }

    if ('IntersectionObserver' in window) {
      const sectionObserver = new IntersectionObserver(([entry]) => {
        sectionVisible = entry.isIntersecting;
        if (sectionVisible) {
          playAll();
        } else {
          pauseAll();
        }
      }, {
        rootMargin: '300px 0px',
        threshold: 0.01
      });

      sectionObserver.observe(root);
    } else {
      sectionVisible = true;
      playAll();
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        playAll();
      } else {
        pauseAll();
      }
    });

    updateControls();
  }

  function initAll(scope = document) {
    if (scope.matches?.(selector)) init(scope);
    scope.querySelectorAll?.(selector).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
